import React, { useEffect, useRef, useState } from 'react';
import { Tag } from '../services/tagService';

// List-view tag rendering + filtering (locked UX spec):
//  • Up to 3 flat neutral pills per row, then "+N more". Neither tag table
//    has a color field — do not invent one.
//  • Pills are display-only; clicking one does nothing.
//  • Filtering is a dedicated multi-select control with OR logic.

const PILL: React.CSSProperties = {
  display: 'inline-block',
  background: 'var(--surface-2)', color: 'var(--ink-2)',
  border: '0.5px solid var(--border-light)',
  borderRadius: 'var(--r-sm)', padding: '1px 7px',
  fontSize: 11, fontWeight: 500, fontFamily: 'var(--font-ui)',
  whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
  verticalAlign: 'middle',
};

const MAX_VISIBLE = 3;

export function TagPills({ tags }: { tags: Tag[] }) {
  if (!tags.length) return null;
  const visible = tags.slice(0, MAX_VISIBLE);
  const overflow = tags.length - visible.length;
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {visible.map(t => (
        <span key={t.id} style={PILL} title={t.name}>{t.name}</span>
      ))}
      {overflow > 0 && (
        <span
          style={{ ...PILL, color: 'var(--ink-4)' }}
          title={tags.slice(MAX_VISIBLE).map(t => t.name).join(', ')}
        >+{overflow} more</span>
      )}
    </span>
  );
}

interface FilterProps {
  options: Tag[];             // tags available to filter on
  selected: string[];         // selected tag ids
  onChange: (ids: string[]) => void;
  /** Extra style merged into the trigger button (e.g. to match a
   *  neighbouring button's height so they align on the same row). */
  triggerStyle?: React.CSSProperties;
  /** Anchor the dropdown to the trigger's right edge instead of its left,
   *  so it doesn't overflow the viewport when placed near the screen edge. */
  align?: 'left' | 'right';
}

/** Multi-select tag filter dropdown. OR logic is the caller's contract:
 *  show rows matching at least one selected tag. */
export function TagMultiFilter({ options, selected, onChange, triggerStyle, align = 'left' }: FilterProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const active = selected.length > 0;

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          fontFamily: 'var(--font-ui)', fontSize: 13,
          padding: '5px 10px', borderRadius: 'var(--r-sm)',
          border: '1px solid ' + (active ? 'var(--brand)' : 'var(--border)'),
          background: active ? 'var(--brand-mist)' : 'var(--surface)',
          color: active ? 'var(--brand)' : 'var(--ink-2)',
          cursor: 'pointer', whiteSpace: 'nowrap',
          ...triggerStyle,
        }}
      >
        {active ? `Tags (${selected.length})` : 'Any tag'} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', marginTop: 4,
          ...(align === 'right' ? { right: 0 } : { left: 0 }),
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-panel)',
          zIndex: 60, minWidth: 200, maxHeight: 240, overflowY: 'auto', padding: 6,
        }}>
          {options.length === 0 ? (
            <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--ink-4)' }}>No tags in use.</div>
          ) : (
            options.map(t => (
              <label
                key={t.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 8px', fontSize: 13, color: 'var(--ink)',
                  borderRadius: 'var(--r-sm)', cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(t.id)}
                  onChange={() => toggle(t.id)}
                  style={{ margin: 0 }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
              </label>
            ))
          )}
          {active && (
            <button
              type="button"
              onClick={() => { onChange([]); setOpen(false); }}
              style={{
                width: '100%', marginTop: 4, padding: '5px 8px',
                background: 'transparent', border: 'none',
                borderTop: '0.5px solid var(--border-light)',
                color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer',
                fontFamily: 'var(--font-ui)', textAlign: 'left',
              }}
            >× Clear tag filter</button>
          )}
        </div>
      )}
    </div>
  );
}
